/**
 * Infinite Scroll Component
 *
 * Handles infinite scrolling for search results and collection pages.
 *
 * Usage in Liquid:
 * <div x-data="infiniteScroll"
 *      data-base-url="{{ request.path }}"
 *      data-current-page="{{ paginate.current_page }}"
 *      data-total-pages="{{ paginate.pages }}"
 *      data-search-terms="{{ search.terms | escape }}">
 */

export default function infiniteScroll() {
  return {
    loading: false,
    currentPage: 1,
    totalPages: 1,
    baseUrl: '',
    searchTerms: '',
    hasMore: true,
    observer: null,

    init() {
      const el = this.$el
      this.baseUrl = el.dataset.baseUrl || window.location.pathname
      this.currentPage = parseInt(el.dataset.currentPage) || 1
      this.totalPages = parseInt(el.dataset.totalPages) || 1
      this.searchTerms = el.dataset.searchTerms || ''
      this.hasMore = this.currentPage < this.totalPages

      this.$nextTick(() => {
        this.setupObserver()
      })
    },

    setupObserver() {
      const sentinel = this.$refs.loadMoreSentinel
      if (!sentinel) return

      const options = {
        root: null,
        rootMargin: '200px',
        threshold: 0
      }

      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && this.hasMore && !this.loading) {
            this.loadMore()
          }
        })
      }, options)

      this.observer.observe(sentinel)
    },

    async loadMore() {
      if (this.loading || !this.hasMore) return

      this.loading = true
      const nextPage = this.currentPage + 1

      try {
        let url = `${this.baseUrl}?page=${nextPage}`

        if (this.searchTerms) {
          url += `&q=${encodeURIComponent(this.searchTerms)}`
        }

        const response = await fetch(url, {
          headers: {
            'X-Requested-With': 'XMLHttpRequest'
          }
        })

        if (!response.ok) {
          throw new Error('Failed to load more results')
        }

        const html = await response.text()
        const parser = new DOMParser()
        const doc = parser.parseFromString(html, 'text/html')

        const newItems = doc.querySelectorAll('[data-search-result-item]')

        if (newItems.length > 0) {
          const grid = this.$refs.resultsGrid

          newItems.forEach(item => {
            grid.appendChild(item.cloneNode(true))
          })

          this.currentPage = nextPage
          this.hasMore = nextPage < this.totalPages
        } else {
          this.hasMore = false
        }
      } catch (error) {
        console.error('Error loading more results:', error)
        this.hasMore = false
      } finally {
        this.loading = false
      }
    },

    destroy() {
      if (this.observer) {
        this.observer.disconnect()
      }
    }
  }
}
